import { type HTMLAttributes, type Ref, type ReactNode } from 'react';
import clsx from 'clsx';
import './Typography.css';

export type HeadingLevel = 1 | 2 | 3 | 4 | 5;
export type HeadingTone = 'default' | 'inverse' | 'primary' | 'accent' | 'danger' | 'muted';

export interface HeadingProps extends Omit<HTMLAttributes<HTMLHeadingElement>, 'children'> {
  level?: HeadingLevel;
  tone?: HeadingTone;
  /** Render as a different heading tag. Default = `h{level}`. */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  children: ReactNode;
  ref?: Ref<HTMLHeadingElement>;
}

/**
 * Heading — semantic heading with token sizing.
 * Level controls size (1=display 40 → 5=lg 18). `as` controls the HTML tag
 * independently of level so visual and document-outline can decouple.
 */
export function Heading({
  level = 2,
  tone = 'default',
  as,
  className,
  children,
  ref,
  ...rest
}: HeadingProps) {
  const Tag = as ?? (`h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5');
  return (
    <Tag
      ref={ref}
      className={clsx('fluent-heading', className)}
      data-level={level}
      data-tone={tone === 'default' ? undefined : tone}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export type TextSize = 'xs' | 'sm' | 'base' | 'lg';
export type TextWeight = 'regular' | 'medium' | 'bold' | 'black';
export type TextTone =
  | 'default'
  | 'inverse'
  | 'muted'
  | 'soft'
  | 'primary'
  | 'accent'
  | 'danger'
  | 'info';

export interface TextProps extends Omit<HTMLAttributes<HTMLParagraphElement>, 'children'> {
  size?: TextSize;
  weight?: TextWeight;
  tone?: TextTone;
  align?: 'start' | 'center' | 'end';
  as?: 'p' | 'span' | 'div';
  children: ReactNode;
  ref?: Ref<HTMLParagraphElement>;
}

/**
 * Text — body copy. Sensible defaults (base/regular/default). Use size and
 * weight props instead of inline style to stay on-scale.
 */
export function Text({
  size = 'base',
  weight = 'regular',
  tone = 'default',
  align,
  as: Tag = 'p',
  className,
  children,
  ref,
  ...rest
}: TextProps) {
  return (
    <Tag
      ref={ref as never}
      className={clsx('fluent-text', className)}
      data-size={size}
      data-weight={weight}
      data-tone={tone === 'default' ? undefined : tone}
      data-align={align}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export interface MutedProps extends Omit<HTMLAttributes<HTMLParagraphElement>, 'children'> {
  children: ReactNode;
  as?: 'p' | 'span' | 'div';
  ref?: Ref<HTMLParagraphElement>;
}

/**
 * Muted — small secondary paragraph. Shortcut for <Text size="sm" tone="muted" />
 * that most "hint" / "byline" uses reach for.
 */
export function Muted({ as: Tag = 'p', className, children, ref, ...rest }: MutedProps) {
  return (
    <Tag ref={ref as never} className={clsx('fluent-muted', className)} {...rest}>
      {children}
    </Tag>
  );
}
